/**
 * RESPONSIBILITY: which sections make up the home page, and in what order.
 *
 * One band, one section, one pencil. Editing the hero is not the same save as editing
 * the give card three screens down, so they are not the same section.
 */
import { SectionName, type EventRow, type Ministry } from '@app/sections';
import type { ImageValue } from '@framework/core';
import { SectionUpdate } from '../cms';
import {
  HomeHero, HomeMission, HomeEvents, HomeMinistries, HomeSermon, HomeGive, HomeVisit,
  BulletinLink,
} from '../site';

export interface HomePageProps {
  events: EventRow[];
  ministries: Ministry[];
  /** The appeal's own photograph. It lives on the House of Honor section, not on this band. */
  appealImage: ImageValue | undefined;
}

export function HomePage({ events, ministries, appealImage }: HomePageProps) {
  return (
    <main id="home" data-anchor="home">
      <SectionUpdate name={SectionName.HomeHero}>
        {(c) => <HomeHero c={c} />}
      </SectionUpdate>

      {/* The bulletin link sits in the welcome card's service panel but is the Bulletin
          section's. No pencil here — two in one card is a guess which opens what; it is
          edited on the Bulletin page. */}
      <SectionUpdate name={SectionName.HomeMission}>
        {(c) => (
          <HomeMission
            c={c}
            extra={(
              <SectionUpdate name={SectionName.Bulletin} pencil="none">
                {(b) => <BulletinLink c={b} />}
              </SectionUpdate>
            )}
          />
        )}
      </SectionUpdate>

      <SectionUpdate name={SectionName.HomeEvents}>
        {(c) => <HomeEvents c={c} events={events.slice(0, c.count || 7)} />}
      </SectionUpdate>

      <SectionUpdate name={SectionName.HomeSermon}>
        {(c) => <HomeSermon c={c} />}
      </SectionUpdate>

      <SectionUpdate name={SectionName.HomeMinistries}>
        {(c) => <HomeMinistries c={c} ministries={ministries} />}
      </SectionUpdate>

      <SectionUpdate name={SectionName.HomeGive}>
        {(c) => <HomeGive c={c} image={appealImage} />}
      </SectionUpdate>

      <SectionUpdate name={SectionName.HomeVisit}>
        {(c) => <HomeVisit c={c} />}
      </SectionUpdate>
    </main>
  );
}
